import {IBookingPsoViewModel} from "./booking-pso.view-mode.interface";
import {BookingModel} from "../booking.model";
import {IServiceFactory} from "../../../service-factory.interface";
import {PsoUserAgreementEnum, PsoUserOptionsEnum} from "../../../flight-search/flight-search-controller/pso.enums";
import {BookingFlowTypeEnum} from "../../booking-strategies/booking-flow-type.enum";
import {IBookingStrategyToBookingAdapter} from "../../booking-strategies/booking-strategy-to-booking.adapter.interface";
import {JourneyModel} from "../journey/journey.model";
import {IPsoMarket, IPsoMarketDiscountOptions} from "../../../stations/pso-market.interface";
import {Lazy} from "../../../../utils/lazy";
import {PsoWorkersAgreementsModel} from "./agreements/workers/pso-workers-agreements.model";
import {PsoFamilyAgreementsModel} from "./agreements/families/pso-family-agreements.model";
import {IPassengerPsoCategoryViewModel} from "../passenger/passenger-pso-category.view-model.interface";

export class BookingPsoModel implements IBookingPsoViewModel {
    constructor(private readonly booking: BookingModel) {

    }

    private get services(): IServiceFactory {
        return this.booking.services;
    }

    private get bookingStrategy(): IBookingStrategyToBookingAdapter {
        return this.booking.bookingStrategyAdapter;
    }

    private get isOnBookingFlow(): boolean {
        return this.bookingStrategy.bookingFlowType === BookingFlowTypeEnum.Booking;
    }

    get isPsoAcceptedByUser(): boolean {
        return this.bookingStrategy.flightSearchController.psoUserAgreement === PsoUserAgreementEnum.Agree;
    }

    private isJourneyEligibleForPsoDiscount(journey: JourneyModel, departureLimitDate: Date): boolean {

        const departureDate = journey.designator.departureDate;

        return departureDate.getTime() <= departureLimitDate.getTime();
    }

    get shouldAskForPsoUserAgreement(): boolean {
        return (this.isOnBookingFlow
                && this.bookingStrategy.flightSearchController.psoUserOption !== PsoUserOptionsEnum.None
                && this.bookingStrategy.flightSearchController.psoUserAgreement === PsoUserAgreementEnum.None);
    }

    get psoMarket(): IPsoMarket | null {

        const journey = this.booking.departureJourney;

        if(!journey) {
            return null;
        }

        return this.services.stations.getPsoMarket(journey.designator.origin, journey.designator.destination, {
            bookingDate: this.booking.getBookedDate(),
            departureDate: journey.designator.departureDate,
        });
    }

    private get psoDiscountOptions(): IPsoMarketDiscountOptions | null {

        if(!this.isOnBookingFlow) {
            return null;
        }

        if(!this.isPsoAcceptedByUser) {
            return null;
        }

        const psoMarket = this.psoMarket;


        if(!psoMarket?.discountOptions) {
            return null;
        }

        if(!psoMarket.isApplicableAtBookingDate(this.booking.getBookedDate())) {
            return null;
        }

        return psoMarket.discountOptions;
    }

    get allowReducedMobility(): boolean {
        return this.isPsoAcceptedByUser && Boolean(this.psoMarket?.allowReducedMobility);
    }

    get allowStudent(): boolean {
        return this.isPsoAcceptedByUser && Boolean(this.psoMarket?.allowStudent);
    }

    get allowRetired(): boolean {
        return this.isPsoAcceptedByUser && Boolean(this.psoMarket?.allowRetired);
    }

    get allowMedicalReason(): boolean {
        return this.isPsoAcceptedByUser && Boolean(this.psoMarket?.allowMedicalReason);
    }

    getReducedMobilitySsrTranslation(): string {
        if(this.psoMarket) {
            return this.psoMarket.translations.reduceMobilitySsrDescription
        } else {
            return this.services.ssrTypes.PRMP.name;
        }
    }

    get isEligibleForPsoMarketDiscount(): boolean {
        if(!this.psoDiscountOptions) {
            return false;
        }

        return  this.booking.unfilteredJourneys.some(j => this.isJourneyEligibleForPsoDiscount(j, this.psoDiscountOptions!.departureDateUntil));
    }

    private _getPossiblePsoMarkets(): IPsoMarket[] {
        const journey = this.booking.departureJourney;

        if(!journey) {
            return [];
        }
        return this.services.stations.getPossiblePsoMarkets(journey.designator.origin, journey.designator.destination);
    }

    public get shouldRecalculatePsoDiscount(): boolean {

        const possiblePsoMarkets = this._getPossiblePsoMarkets();

        if(this.isOnBookingFlow && possiblePsoMarkets.some(psoMarket => psoMarket.discountOptions?.hasPsoDiscountFee(this.booking))) {
            return true;
        }

        return this.isEligibleForPsoMarketDiscount;
    }


    async applyPsoMarketDiscount(): Promise<void> {
        if(!this.shouldRecalculatePsoDiscount) {
            return;
        }

        let discountOptions = this.psoDiscountOptions;

        if(!discountOptions) {
            discountOptions = this._getPossiblePsoMarkets().find(psoMarket => psoMarket.discountOptions?.hasPsoDiscountFee(this.booking))?.discountOptions ?? null;
        }

        if(discountOptions) {
            await discountOptions.applyDiscount(this.booking);
        }
    }

    private readonly _workersAgreements: Lazy<PsoWorkersAgreementsModel> = new Lazy<PsoWorkersAgreementsModel>(() => {
        return new PsoWorkersAgreementsModel(this.booking)
    });

    get workersAgreements(): PsoWorkersAgreementsModel {
        return this._workersAgreements.value;
    }

    private readonly _familyAgreements: Lazy<PsoFamilyAgreementsModel> = new Lazy<PsoFamilyAgreementsModel>(() => {
        return new PsoFamilyAgreementsModel(this.booking)
    });

    get familyAgreements(): PsoFamilyAgreementsModel {
        return this._familyAgreements.value;
    }

    get hasValidPsoAgreements(): boolean {
        return this.workersAgreements.hasValidWorkersAgreements && this.familyAgreements.hasValidWorkersAgreements;
    }

    getPassengerCategoriesForAdult(): IPassengerPsoCategoryViewModel[] {
        return this._getPassengerCategories((psoMarket: IPsoMarket) => psoMarket.getAdultPassengerCategories(this.bookingStrategy.flightSearchController.psoUserOption));
    }

    getPassengerCategoriesForChild(): IPassengerPsoCategoryViewModel[] {
        return this._getPassengerCategories((psoMarket: IPsoMarket) => psoMarket.getChildPassengerCategories(this.bookingStrategy.flightSearchController.psoUserOption));
    }

    getPassengerCategoriesForInfant(): IPassengerPsoCategoryViewModel[] {
        return this._getPassengerCategories((psoMarket: IPsoMarket) => psoMarket.getInfantPassengerCategories(this.bookingStrategy.flightSearchController.psoUserOption));
    }

    private _getPassengerCategories(selector: (psoMarket: IPsoMarket) => IPassengerPsoCategoryViewModel[]): IPassengerPsoCategoryViewModel[] {
        if(!this.isOnBookingFlow) {
            return [];
        }

        const psoMarket = this.psoMarket;
        if(!psoMarket) {
            return [];
        }

        if(!this.isPsoAcceptedByUser) {
            return [];
        }

        return selector(psoMarket);
    }

}